/* eslint-disable @typescript-eslint/no-inferrable-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/naming-convention */
/**
 * This file was auto-generated (npm run generate:api).
 * Do not make direct changes to the file.
 */
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { components, paths } from 'interfaces/api/users';
import { del, get, patch, post, put } from 'shared/utils/api';
import { RequestConfig } from 'shared/utils/api/request';
import { objectToQueryParams } from 'utils/format';
import { identity } from 'utils/identity';

/**
 * `/users/{userId}/recovery`: Get registered password recovery options for a logged in persona
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchRecoveryByUserId = (
  userId: number,
  requestConfig: RequestConfig | null = null,
): Promise<
  components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.RecoveryInformation']
> =>
  get({
    url: `${userId}/recovery`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/recovery`: Get registered password recovery options for a logged in persona
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useRecoveryByUserId = <
  TOut = components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.RecoveryInformation'],
>(
  userId: number,
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<
    components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.RecoveryInformation'],
    AxiosError<any>,
    TOut,
    any[]
  > | null = null,
) =>
  useQuery(
    ['/users/{userId}/recovery', identity.userId, userId],
    () => fetchRecoveryByUserId(userId, requestConfig),
    queryConfig || {},
  );

/**
 * `/users/{userId}/recovery/email`: Initiate a change in password recovery email for a logged in persona.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing emailAddress
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitRecoveryEmailByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.UpdateRecoveryEmail'],
  requestConfig: RequestConfig | null = null,
): Promise<
  components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.UpdateRecoveryStatus']
> =>
  put({
    url: `${userId}/recovery/email`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/recovery/phone`: Initiate a change in password recovery for mobile for a logged in persona.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing phoneNumber
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitRecoveryPhoneByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.UpdateRecoveryPhone'],
  requestConfig: RequestConfig | null = null,
): Promise<
  components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.UpdateRecoveryStatus']
> =>
  put({
    url: `${userId}/recovery/phone`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/recovery/resend`: Resend verification code for an already initiated but not finished account recovery information update flow
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing correlationToken
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitRecoveryResendByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.ResendRecoveryUpdate'],
  requestConfig: RequestConfig | null = null,
): Promise<any> =>
  post({
    url: `${userId}/recovery/resend`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/recovery/activate`: Finish an Account Recovery Information update (e.g. setting a new or changing existing contact info for password recovering
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing verificationCode and correlationToken
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitRecoveryActivateByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.Recovery.ActivateRecoveryUpdate'],
  requestConfig: RequestConfig | null = null,
): Promise<any> =>
  post({
    url: `${userId}/recovery/activate`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/impersonate`: Get an AD accountable impersonation token for provided username
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitImpersonate = (
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.RequestImpersonation'],
  requestConfig: RequestConfig | null = null,
): Promise<components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserImpersonation']> =>
  post({
    url: `impersonate`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/profile/my`: Returns info for the authorized user identified by userId/personaId.
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchMyProfile = (
  requestConfig: RequestConfig | null = null,
): Promise<components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile']> =>
  get({
    url: `profile/my`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/profile/my`: Returns info for the authorized user identified by userId/personaId.
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useMyProfile = <
  TOut = components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile'],
>(
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<
    components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile'],
    AxiosError<any>,
    TOut,
    any[]
  > | null = null,
) =>
  useQuery(
    ['/users/profile/my', identity.userId],
    () => fetchMyProfile(requestConfig),
    queryConfig || {},
  );

/**
 * `/users/profile/{username}/availability`: Checks if combination of username and user login is available on system
 * @param username  Username to check for availability
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchProfileAvailabilityByUsername = (
  username: string,
  requestConfig: RequestConfig | null = null,
): Promise<boolean> =>
  get({
    url: `profile/${username}/availability`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/profile/{username}/availability`: Checks if combination of username and user login is available on system
 * @param username  Username to check for availability
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useProfileAvailabilityByUsername = <TOut = boolean>(
  username: string,
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<boolean, AxiosError<any>, TOut, any[]> | null = null,
) =>
  useQuery(
    ['/users/profile/{username}/availability', identity.userId, username],
    () => fetchProfileAvailabilityByUsername(username, requestConfig),
    queryConfig || {},
  );

/**
 * `/users/{userId}/profile`: Returns info for the user identified by userId/personaId.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchProfileByUserId = (
  userId: number,
  requestConfig: RequestConfig | null = null,
): Promise<components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile']> =>
  get({
    url: `${userId}/profile`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/profile`: Returns info for the user identified by userId/personaId.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useProfileByUserId = <
  TOut = components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile'],
>(
  userId: number,
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<
    components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfile'],
    AxiosError<any>,
    TOut,
    any[]
  > | null = null,
) =>
  useQuery(
    ['/users/{userId}/profile', identity.userId, userId],
    () => fetchProfileByUserId(userId, requestConfig),
    queryConfig || {},
  );

/**
 * `/users/{userId}/profile/username`: Update username for an existing user.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing username
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitProfileUsernameByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UpdateUsername'],
  requestConfig: RequestConfig | null = null,
): Promise<any> =>
  put({
    url: `${userId}/profile/username`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/{userId}/profile/password`: Update password for an existing user.
 * @param userId  PersonaId for the customer, for which recovery email should be updated
 * @param data  Payload containing old and new password
 * @param requestConfig Any additional properties to be added to the request
 */
export const submitProfilePasswordByUserId = (
  userId: number,
  data: components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UpdatePassword'],
  requestConfig: RequestConfig | null = null,
): Promise<any> =>
  put({
    url: `${userId}/profile/password`,
    apiSegment: 'users',
    data,
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/my/profile/services`: Returns services for the authorized user identified by userId/personaId.
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchMyProfileServices = (
  requestConfig: RequestConfig | null = null,
): Promise<components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfileService'][]> =>
  get({
    url: `my/profile/services`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/my/profile/services`: Returns services for the authorized user identified by userId/personaId.
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useMyProfileServices = <
  TOut = components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfileService'][],
>(
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<
    components['schemas']['Nuuday.NextGen.UserProfile.Contracts.UserProfileService'][],
    AxiosError<any>,
    TOut,
    any[]
  > | null = null,
) =>
  useQuery(
    ['/users/my/profile/services', identity.userId],
    () => fetchMyProfileServices(requestConfig),
    queryConfig || {},
  );

/**
 * `/users/version`: No summary available
 * @param requestConfig Any additional properties to be added to the request
 */
export const fetchVersion = (requestConfig: RequestConfig | null = null): Promise<any> =>
  get({
    url: `version`,
    apiSegment: 'users',
    requireAuth: true,
    ...(requestConfig || {}),
  });

/**
 * `/users/version`: No summary available
 * @param requestConfig Any additional properties to be added to the request
 * @param queryConfig  Optional configuration object for React Query
 */
export const useVersion = <TOut = any>(
  requestConfig: RequestConfig | null = null,
  queryConfig: UseQueryOptions<any, AxiosError<any>, TOut, any[]> | null = null,
) =>
  useQuery(
    ['/users/version', identity.userId],
    () => fetchVersion(requestConfig),
    queryConfig || {},
  );
